import { type FC, memo, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { Grid, Label } from '@wf/sw-ui-kit';

import TermOfDeliveryAndInvoice from './TermOfDeliveryAndInvoice';
import CountryOfExportOrImport from '../CountryOfExportOrImport';
import PlaceOfLoadingsFields from './PlaceOfLoadingsFields';
import Separator from '../../../../components/Separator';
import ActionDatesFields from './ActionDatesFields';
import AdditionalFields from '../AdditionalFields';
import { LicenseField } from '../LicenseField';
import MainInfoFields from './MainInfoFields';

import useDepartmentOfficeOptions from '../../hooks/useDepartmentOfficeOptions';
import useLicenseAbbreviation from '../../../../hooks/useLicenseAbbreviation';
import { getTypeOfUseSelectOptions } from '../../constants/selectOptions';
import { FieldPrefix } from '../../constants/fieldPrefixes';
import { HeaderTabFields } from '../../types/fieldEnums';
import { LicenseTypeNature } from '../../../../types';
import { Flow, Tab } from '../../types/enums';

import { fieldConfigsSelector, selectedLicenseTypeSelector } from '../../store/selectors';
import { fieldNameConverter } from '../../helpers/fieldNameConverter';
import { RimmEntityType } from '../../../rimm/data/rimmEntityEnums';

const HeaderTab: FC = () => {
  const { t } = useTranslation();
  const fieldConfigs = useSelector(fieldConfigsSelector);
  const licenseAbbreviation = useLicenseAbbreviation();
  const typeOfUseOptions = useMemo(() => getTypeOfUseSelectOptions(t), [t]);

  const { data: selectedLicenseType } = useSelector(selectedLicenseTypeSelector);

  const licenseTypeNature = selectedLicenseType?.licenseTypeResponse.licenseTypeNature;

  const formFlow = useWatch({ name: HeaderTabFields.flow });

  const customsOfficeHelpText =
    formFlow === Flow.EX
      ? 'Customs office from where the goods are exported from the country'
      : 'Customs office where the goods enter the country';

  const countryOfExportOrImportEnabled =
    fieldConfigs[fieldNameConverter(HeaderTabFields.countryOfDestinationCode)]?.hidden === false;
  const departmentOfficeEnabled =
    fieldConfigs[fieldNameConverter(HeaderTabFields.departmentOfficeCode)]?.hidden === false;
  const typeOfUseEnabled = fieldConfigs[fieldNameConverter(HeaderTabFields.typeOfUse)]?.hidden === false;
  const entryExitPointEnabled = fieldConfigs[fieldNameConverter(HeaderTabFields.customsOfficeCode)]?.hidden === false;

  const showCountryOfExportOrImportField =
    countryOfExportOrImportEnabled && licenseTypeNature !== LicenseTypeNature.EPHYTO;

  const showCountryOfExportSection =
    entryExitPointEnabled || showCountryOfExportOrImportField || departmentOfficeEnabled || typeOfUseEnabled;

  const departmentOfficeOptions = useDepartmentOfficeOptions();
  const assignmentDivisions = useMemo(() => {
    const approvals = selectedLicenseType?.licenseTypeResponse?.approvals;
    const transitions = approvals?.[0]?.transitions;
    const manualTransition = transitions?.find((i) => i.isManualAssignment);

    return manualTransition && 'assignmentDivisions' in manualTransition
      ? (manualTransition.assignmentDivisions as Record<string, unknown>)
      : {};
  }, [selectedLicenseType]);

  const filteredDepartmentOfficeOptions = departmentOfficeOptions.filter((o) =>
    Object.keys(assignmentDivisions).includes(o.value)
  );

  return (
    <>
      <Grid columns="repeat(3, minmax(0, 1fr))">
        <MainInfoFields />
        <Separator className="grid-full-width" />
        <ActionDatesFields />
        {showCountryOfExportSection && (
          <>
            <Separator className="grid-full-width" />
            {showCountryOfExportOrImportField && <CountryOfExportOrImport />}
            {entryExitPointEnabled && (
              <LicenseField.RimmSelectField
                label={
                  <Label helpText={t(customsOfficeHelpText)}>
                    {t(formFlow === Flow.IM ? 'Entry Point' : 'Exit Point')}
                  </Label>
                }
                configName={HeaderTabFields.customsOfficeCode}
                name={HeaderTabFields.customsOfficeCode}
                entity={RimmEntityType.customsOffice}
                placeholder={t('Select...')}
                withTag
              />
            )}
            {departmentOfficeEnabled && (
              <LicenseField.SelectField
                options={
                  Object.keys(assignmentDivisions).length ? filteredDepartmentOfficeOptions : departmentOfficeOptions
                }
                label={
                  <Label
                    helpText={t(
                      'Department Office responsible for processing and approval of {{licenseAbbreviation}} application',
                      { licenseAbbreviation }
                    )}
                  >
                    {t('Department Office')}
                  </Label>
                }
                configName={HeaderTabFields.departmentOfficeCode}
                name={HeaderTabFields.departmentOfficeCode}
                placeholder={t('Department Office')}
                isClearable
              />
            )}
            {typeOfUseEnabled && (
              <LicenseField.SelectField
                label={
                  <Label
                    helpText={t(
                      'The mode of use of {{licenseAbbreviation}} document. Whether it can be used in the declaration only once (Single use) or several times (Multiple use)',
                      { licenseAbbreviation }
                    )}
                  >
                    {t('Type of Use')}
                  </Label>
                }
                configName={HeaderTabFields.typeOfUse}
                placeholder={t('Choose type of use')}
                name={HeaderTabFields.typeOfUse}
                options={typeOfUseOptions}
                isClearable
              />
            )}
          </>
        )}
        <TermOfDeliveryAndInvoice />
        <PlaceOfLoadingsFields />
      </Grid>
      <AdditionalFields tab={Tab.HEADER} configNamePrefix={FieldPrefix.lpco} />
    </>
  );
};

export default memo(HeaderTab);
